import { useDispatch } from "react-redux";
import { updateConfig } from "../../../store/slices/Orders/orderSlice";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig as updateStaffBillingConfig } from "../../../store/slices/Billing/staffBillingSlice";
import { updateOrderData } from "../../../store/slices/Orders/addOrderSlice";
import { useSelector } from "react-redux";
import {
  useGetOrdersListDataQuery,
  useGetOldCertificateListDataQuery,
  useUpdateOrderTableHeadDataMutation,
} from "../../../store/queries/Orders";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  downloadCashReceipt,
  deleteOrder,
  getOrderData,
  getPrintData,
  getVerificationExcelDownload,
  getMultiplePrintData,
} from "./api";
import html2pdf from "html2pdf.js";
import moment from "moment";
import { subDays } from "date-fns";
import { debounce } from "lodash";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { DownloadExcelFiles } from "./api";
import { useLocation } from "react-router-dom";
import getFilterCount from "../../../utils/getFilterCount";

const defaultStartDate = new Date(subDays(new Date(), 30))
  .toISOString()
  .split("T")[0];
const defaultEndDate = new Date(new Date()).toISOString().split("T")[0];

const useOrder = () => {
  let paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];
  const location = useLocation();
  const dispatch = useDispatch();
  const [inputData, setInputData] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get("activeTab");
  const [updateOrderFields] = useUpdateOrderTableHeadDataMutation();
  const { tableFields, showEditModal, dashboard_permissions } = useSelector(
    (state) => state.global,
  );
  const iSUploadAdditionalDocument =
    dashboard_permissions?.menu_permissions.some(
      (item) => item?.menu_name === "Upload Additional Document",
    );
  // eslint-disable-next-line no-unused-vars
  const [date, setDate] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });
  const orderState = useSelector((state) => state.order);

  const stockPrintRef = useRef(null);
  const cashReceiptRef = useRef(null);

  const isOldCertificateTab = activeTab === "old-certificate";

  const listQueryArgs = {
    per_page: orderState.currentPageSize,
    page: orderState.currentPage,
    sort_by: orderState.sortBy,
    sort_order: orderState.sortOrder,
    start: orderState.startDate,
    end: orderState.endDate,
    search: orderState.search,
    date: orderState?.filter?.date,
    item_type: orderState.filter.item_type,
    shop: orderState.filter.shop,
    status: orderState.filter.status,
  };

  const newCertificateResult = useGetOrdersListDataQuery(listQueryArgs, {
    skip: isOldCertificateTab,
  });
  const oldCertificateResult = useGetOldCertificateListDataQuery(listQueryArgs, {
    skip: !isOldCertificateTab,
  });

  const {
    data: ordersList = {},
    isLoading,
    isFetching,
    refetch,
  } = isOldCertificateTab ? oldCertificateResult : newCertificateResult;

  const tabNavs = [
    {
      label: "New Certificate",
      title: "New Certificate",
      link: `/orders?activeTab=new-certificate`,
      active: !isOldCertificateTab,
    },
    {
      label: "Old Certificate",
      title: "Old Certificate",
      link: `/orders?activeTab=old-certificate`,
      active: isOldCertificateTab,
    },
  ];

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        let fields;
        if (ordersList?.data?.fields) {
          if (isOldCertificateTab) {
            // Old Certificate tab: drop the Document, Advance Payment, Eway Bill
            // and Delivery Challan columns (and their downloads).
            fields = Object.fromEntries(
              Object.entries(ordersList.data.fields).filter(
                ([key, value]) =>
                  key !== "document" &&
                  key !== "eway_bill_download" &&
                  key !== "delivery_challan_download" &&
                  !/advance\s*payment/i.test(value?.label ?? ""),
              ),
            );
          } else {
            fields = {
              ...ordersList.data.fields,
              eway_bill_download: { label: "Eway Bill", sortable: false },
              delivery_challan_download: {
                label: "Delivery Challan",
                sortable: false,
              },
            };
          }
        } else {
          fields = ordersList?.data?.fields;
        }
        state.tableFields = fields;
      }),
    );
    // eslint-disable-next-line
  }, [ordersList]);
  useEffect(() => {
    if (orderState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [orderState.clearSelection]);

  useEffect(() => {
    if (orderState?.selectedOrder && orderState?.isEdit) {
      fillOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState?.isEdit, orderState?.showAddOrderModal]);

  //Reset to initial state;
  useEffect(() => {
    setInputData("");
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
        state.currentPageSize = 20;
      }),
    );
  }, [activeTab]);

  const handleCloseAddOrderModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddOrderModal = false;
        state.isEdit = false;
        state.isOldCertificate = false;
      }),
    );
  };

  const handleAddOrderClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddOrderModal = true;
        state.isEdit = false;
        state.isOldCertificate = false;
        state.orderData = [];
      }),
    );
    dispatch(
      updateOrderData((state) => {
        state.currentFormData = {};
        state.activeTab = "Collection";
        state.isEditProfile = false;
        state.completedTabs = {
          ...state.completedTabs,
          Collection: false,
          Item: false,
          Service: false,
          Engraving: false,
        };
        state.CompletedPercentage = {
          ...state.CompletedPercentage,
          Collection: 0,
          Item: 0,
          Service: 0,
          Engraving: 0,
        };
      }),
    );
  };

  const handleAddOldCertificateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddOrderModal = true;
        state.isEdit = false;
        state.isOldCertificate = true;
        state.orderData = [];
      }),
    );
    dispatch(
      updateOrderData((state) => {
        state.currentFormData = {};
        state.activeTab = "Collection";
        state.isEditProfile = false;
        state.completedTabs = {
          ...state.completedTabs,
          Collection: false,
          Item: false,
          Service: false,
        };
        state.CompletedPercentage = {
          ...state.CompletedPercentage,
          Collection: 0,
          Item: 0,
          Service: 0,
        };
      }),
    );
  };

  const fillOrderData = () => {
    getOrderData(orderState?.selectedOrder).then((response) => {
      if (response?.data?.status_code === 200) {
        dispatch(
          updateConfig((state) => (state.orderData = response?.data?.data)),
        );
      } else {
        toast.error("Something went wrong to fetch data");
      }
    });
  };

  const handleEditClick = (e) => {
    dispatch(updateOrderData((state) => (state.activeTab = "Collection")));
    dispatch(
      updateConfig((state) => {
        state.showAddOrderModal = true;
        state.selectedOrder = e?.[0];
        state.isEdit = true;
        // Editing a row from the Old Certificate tab opens the old-certificate
        // form (no Engraving); the New Certificate tab keeps the standard form.
        state.isOldCertificate = isOldCertificateTab;
      }),
    );
  };

  const handleOpenUploadModal = (id) => {
    dispatch(
      updateConfig((state) => {
        state.showUploadModal = true;
        state.stock_receipt_id = id;
      }),
    );
  };
  const handleCloseUploadModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showUploadModal = false;
        state.stock_receipt_id = "";
      }),
    );
  };

  const handleDownload = (id) => {
    DownloadExcelFiles(id).then((response) => {
      if (response?.data?.status_code === 200) {
        const url = response?.data?.data?.url;
        const link = document.createElement("a");
        link.href = url;
        link.download = url.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (response?.status_code === 403) {
        toast.error("No Data available for download");
      }
    });
  };
  const handleDownloadCashReceipt = async (id, paymentMode) => {
    if (!id) {
      toast.error("Invalid stock receipt");
      return;
    }
    try {
      const response = await downloadCashReceipt(id);
      if (response?.data?.status_code !== 200) {
        toast.error(response?.message ?? "Failed to download cash receipt");
        return;
      }

      const receiptData = response?.data?.data;
      if (!receiptData) {
        toast.error("Cash receipt data not available");
        return;
      }

      dispatch(
        updateStaffBillingConfig((state) => {
          state.receiptPrintData = {
            ...receiptData,
            payment_method: receiptData?.payment_method ?? paymentMode,
          };
          state.clear = true;
        }),
      );

      if (response?.message) {
        toast.success(response.message);
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      const element = cashReceiptRef?.current;
      if (!element) {
        toast.error("Receipt template not ready");
        return;
      }

      const pdfObj = await html2pdf(element, {
        margin: [28, 0, 0, 0],
        filename: `${
          "Cash_Receipt" + moment(Date.now()).format("_MM_DD_YYYY_HH_mm_ss")
        }.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a5", orientation: "portrait" },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      })
        .from("element-to-print")
        .outputPdf()
        .get("pdf");

      pdfObj.autoPrint();
      window.open(pdfObj.output("bloburl"), "F");

      dispatch(
        updateStaffBillingConfig((state) => {
          state.clear = false;
        }),
      );
    } catch (error) {
      toast.error("Something went wrong while downloading the cash receipt");
      dispatch(
        updateStaffBillingConfig((state) => {
          state.clear = false;
        }),
      );
    }
  };
  const handleEwayBillDownload = (receiptId, key, fileName) => {
    getVerificationExcelDownload(receiptId, key).then((response) => {
      if (response?.data?.data?.url) {
        const { url, extension } = response.data.data;
        let downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.setAttribute(
          "download",
          `${fileName}_${moment(Date.now()).format("MM_DD_YYYY_HH_mm_ss")}.${extension}`,
        );
        downloadLink.target = "_blank";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        toast.success(`${fileName} downloaded successfully.`);
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    });
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      gli_number: (feild, data) => (
        <>
          <span
            className={
              iSUploadAdditionalDocument
                ? "pro-mb-0 pro-pnt pro-d-flex pro-items-center pro-gap-2"
                : ""
            }
            onClick={
              iSUploadAdditionalDocument
                ? () => handleOpenUploadModal(data?._id)
                : () => {}
            }
            title={iSUploadAdditionalDocument ? "Upload document" : ""}
          >
            {data?.gli_number}{" "}
            {iSUploadAdditionalDocument && (
              <span className="material-symbols-outlined">upload</span>
            )}
          </span>
        </>
      ),
      advance_payment_mode: (feild, data) => (
        <>
          <span
            className={"pro-mb-0 pro-pnt pro-d-flex pro-items-center pro-gap-2"}
            onClick={() =>
              handleDownloadCashReceipt(data?._id, data?.advance_payment_mode)
            }
            title={"Upload document"}
          >
            {data?.advance_payment_mode}{" "}
            {data?.advance_payment_mode && (
              <span className="material-symbols-outlined">download</span>
            )}
          </span>
        </>
      ),
      service: (feild, data) => (
        <ul>
          {data[feild]?.map(({ name }, index) => (
            <li key={index}>{name}</li>
          ))}
        </ul>
      ),
      priority: (_, data) =>
        data?.priority === "Urgent" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Urgent"}
          </span>
        ) : data?.priority === "Normal" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Normal"}
          </span>
        ) : (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {data?.work_status}
          </span>
        ),
      work_status: (_, data) =>
        data?.work_status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.work_status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.work_status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.work_status === "Approved" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Approved"}
          </span>
        ) : data?.work_status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.work_status}
          </span>
        ),
      document: (feild, data) => (
        <p className="pro-mb-0">
          {
            <Button
              className={"pro-btn-primary"}
              onClick={() => handleDownload(data?._id)}
            >
              Download
            </Button>
          }
        </p>
      ),
      eway_bill_download: (_, data) => (
        <p className="pro-mb-0">
          <Button
            className={"pro-btn-primary"}
            onClick={() =>
              handleEwayBillDownload(data?._id, "eway_bill", "Eway_Bill")
            }
          >
            Download
          </Button>
        </p>
      ),
      delivery_challan_download: (_, data) => (
        <p className="pro-mb-0">
          <Button
            className={"pro-btn-primary"}
            onClick={() =>
              handleEwayBillDownload(
                data?._id,
                "delivery_challan",
                "Delivery_Challan",
              )
            }
          >
            Download
          </Button>
        </p>
      ),
    };

    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedOrder = e?.[0];
      }),
    );
  };

  const handleDelete = () => {
    let data = { stock_receipt_id: orderState?.selectedOrder };
    deleteOrder(data).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Deleted successfully");
        handleDeleteModal();
        refetch();
      } else {
        toast.error("Something went wrong");
        handleDeleteModal();
      }
    });
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };
  const handleEditColumnsClick = (label) => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };
  const handleClearClick = () => {
    setInputData("");
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
        state.filter.item_type = "";
        state.filter.shop = "";
        state.filter.status = "";
        state.filter.date = [defaultStartDate, defaultEndDate];
      }),
    );
  };

  const handleSearchDebounced = debounce((value, dispatch) => {
    dispatch(
      updateConfig((state) => {
        state.search = value;
        state.currentPage = 1;
      }),
    );
  }, 1000);

  const handleSearch = (e) => {
    setInputData(e.target.value);
    handleSearchDebounced(e.target.value, dispatch);
  };

  const handleSort = (label) => {
    if (orderState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = orderState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };
  const handlePrintClick = (ids) => {
    let printId = ids?.[0];
    getPrintData(printId).then((response) => {
      if (response?.data?.status_code === 200) {
        // Dispatch the data to Redux
        dispatch(
          updateConfig((state) => (state.printData = response?.data?.data)),
        );

        // Call the print function after the data is successfully set
        printInvoice();
      } else {
        toast.error("Something went wrong");
      }
    });

    const printInvoice = () => {
      const element = stockPrintRef?.current;
      html2pdf(element, {
        margin: [5, 2, 2, 2],
        filename: `${ordersList?.data?.data.find((item) => item?._id === printId).gli_number || ""}_Stock Receipt Slip_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a5", orientation: "portrait" },
        pagebreak: { mode: ["css"] },
        DisablePdfCompression: true,
      })
        .from("element-to-print")
        .outputPdf()
        .get("pdf")
        .then((pdfObj) => {
          pdfObj.autoPrint();
          window.open(pdfObj.output("bloburl"), "F");
        });
    };
  };

  const handleAddShop = () => {
    dispatch(updateConfig((state) => (state.addShopModal = true)));
  };

  const handleCloseAddShopModal = () => {
    dispatch(updateConfig((state) => (state.addShopModal = false)));
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      item_type: orderState.filter.item_type,
      shop: orderState.filter.shop,
      status: orderState.filter.status,
    });
  }, [
    orderState.filter.item_type,
    orderState.filter.shop,
    orderState.filter.status,
  ]);

  const handleFilter = () => {
    dispatch(updateConfig((state) => (state.showFilter = true)));
  };

  const handleFilterClose = () => {
    dispatch(updateConfig((state) => (state.showFilter = false)));
  };

  const handleDateChange = (date) => {
    const startDateUTC = new Date(date.startDate);
    const endDateUTC = new Date(date.endDate);
    const timeZoneOffset = 5.5 * 60 * 60 * 1000;
    const startDateIST = new Date(startDateUTC.getTime() + timeZoneOffset);
    const endDateIST = new Date(endDateUTC.getTime() + timeZoneOffset);
    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const startDateFormatted = formatDate(startDateIST);
    const endDateFormatted = formatDate(endDateIST);
    const dateArray = [startDateFormatted, endDateFormatted];
    dispatch(updateConfig((state) => (state.filter.date = dateArray)));
  };

  return {
    date,
    ordersList: ordersList?.data,
    isFetching,
    isLoading,
    orderState,
    tableFields,
    tabNavs,
    paginationOptions,
    showEditModal,
    stockPrintRef,
    inputData,
    filterCount,
    cashReceiptRef,
    refetch,
    handleDateChange,
    handlePrintClick,
    handleFilter,
    handleSort,
    handleAddShop,
    updateOrderFields,
    handleSearch,
    handleAddOrderClick,
    handleAddOldCertificateClick,
    handleClearClick,
    handlePagination,
    handlePageSize,
    handleCloseAddShopModal,
    handleEditClick,
    handleCloseAddOrderModal,
    handleFilterClose,
    handleEditColumnsClick,
    getRow,
    handleDelete,
    handleDeleteModal,
    handleCloseUploadModal,
    handleOpenUploadModal,
  };
};

export default useOrder;
